import React, { useState, useEffect, useRef } from 'react';
import { store } from '../../app/store';
import { postAPI, formatDate, convertTo12Hour } from '../../app/api';
import { useParams } from 'react-router-dom';
import Loader from '../Universal/Loader';
import ExamAttendComponent from './ExamAttendComponent';
import Wrapper from '../Universal/CustomerWrapper';

// Enhanced security and monitoring features
import Swal from 'sweetalert2';
import './ExamAttend.css';

/**
 * 🚀 ENHANCED EXAM ATTEND SYSTEM - IMPROVEMENTS ADDED:
 * 
 * 🔒 SECURITY ENHANCEMENTS:
 * - Tab switching detection and logging
 * - Keyboard shortcut prevention (F12, Ctrl+C, Ctrl+V, etc.)
 * - Right-click context menu blocking
 * - Fullscreen exit monitoring with warnings
 * - Network connectivity monitoring
 * - Security violation logging and reporting
 * 
 * 📊 PROGRESS TRACKING:
 * - Real-time exam progress bar
 * - Question-wise time tracking
 * - Auto-save functionality every 30 seconds
 * - Enhanced question navigator with time indicators
 * - Detailed exam statistics (answered/skipped/remaining)
 * 
 * 🎯 USER EXPERIENCE:
 * - Interactive question options with hover effects
 * - Character count for descriptive answers
 * - Clear selection option for MCQs
 * - Enhanced visual feedback and status indicators
 * - Comprehensive exam summary before submission
 * - Better responsive design and accessibility
 * 
 * 🛡️ PROCTORING FEATURES:
 * - Face verification status monitoring
 * - Safe distance detection alerts
 * - Security status dashboard
 * - Real-time violation alerts
 * - Connection status monitoring
 */

export default function ExamAttend() {
  const { public_access_token, org_id, user_unique_id } = useParams();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [securityViolations, setSecurityViolations] = useState([]);
  const [keyboardShortcutsDisabled, setKeyboardShortcutsDisabled] = useState(false);
  const [visibilityChangeCount, setVisibilityChangeCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [lastSaveTime, setLastSaveTime] = useState(new Date());
  const containerRef = useRef(null);
  const securityLogRef = useRef([]);

  // Enhanced Security Monitoring
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isFull);
      
      if (examStarted && !isFull) {
        logSecurityViolation('fullscreen_exit', 'Student exited fullscreen mode');
        showSecurityWarning('Fullscreen Exit Detected', 'Please return to fullscreen mode immediately.');
      }

      console.log(isFull ? "✅ Fullscreen mode active" : "❌ Fullscreen mode inactive");
    };

    // Visibility change monitoring (tab switching detection)
    const handleVisibilityChange = () => {
      if (document.hidden && examStarted) {
        setVisibilityChangeCount(prev => prev + 1);
        logSecurityViolation('tab_switch', 'Student switched tabs or minimized browser');
        showSecurityWarning('Tab Switch Detected', 'Switching tabs during exam is not allowed!');
      }
    };

    // Keyboard shortcut prevention
    const handleKeyDown = (e) => {
      if (examStarted && keyboardShortcutsDisabled) {
        // Prevent common shortcuts that could be used to cheat
        const forbiddenKeys = [
          'F12', // Dev tools
          'F11', // Fullscreen toggle (we control this)
        ];

        const forbiddenCombos = [
          { ctrlKey: true, key: 'c' }, // Copy
          { ctrlKey: true, key: 'v' }, // Paste
          { ctrlKey: true, key: 'a' }, // Select all
          { ctrlKey: true, key: 't' }, // New tab
          { ctrlKey: true, key: 'n' }, // New window
          { ctrlKey: true, key: 'w' }, // Close tab
          { ctrlKey: true, key: 'r' }, // Refresh
          { ctrlKey: true, shiftKey: true, key: 'I' }, // Dev tools
          { ctrlKey: true, shiftKey: true, key: 'J' }, // Console
          { ctrlKey: true, shiftKey: true, key: 'C' }, // Inspector
          { altKey: true, key: 'Tab' }, // Alt+Tab
        ];

        if (forbiddenKeys.includes(e.key) || 
            forbiddenCombos.some(combo => 
              Object.keys(combo).every(key => e[key] === combo[key])
            )) {
          e.preventDefault();
          logSecurityViolation('keyboard_shortcut', `Attempted to use ${e.key} ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.altKey ? 'Alt+' : ''}`);
          showSecurityWarning('Shortcut Blocked', 'Keyboard shortcuts are disabled during the exam.');
        }
      }
    };

    // Right-click prevention
    const handleContextMenu = (e) => {
      if (examStarted) {
        e.preventDefault();
        logSecurityViolation('right_click', 'Student attempted to right-click');
        showSecurityWarning('Right-click Disabled', 'Right-click is disabled during the exam.');
      }
    };

    // Network status monitoring
    const handleOnline = () => setConnectionStatus('online');
    const handleOffline = () => {
      setConnectionStatus('offline');
      if (examStarted) {
        logSecurityViolation('network_disconnect', 'Internet connection lost');
        showSecurityWarning('Connection Lost', 'Internet connection lost. Please check your connection.');
      }
    };

    // Add event listeners
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [examStarted, keyboardShortcutsDisabled]);

  
  // Security and monitoring functions
  const logSecurityViolation = (type, description) => {
    const violation = {
      type,
      description,
      timestamp: new Date().toISOString(),
      userId: user_unique_id
    };
    
    securityLogRef.current.push(violation);
    setSecurityViolations(prev => [...prev, violation]);
    
    // Send to backend immediately for critical violations
    if (['fullscreen_exit', 'tab_switch', 'network_disconnect'].includes(type)) {
      reportSecurityViolation(violation);
    }
  };

  const reportSecurityViolation = async (violation) => {
    try {
      const filter = {
        org_id: org_id,
        public_access_token: public_access_token,
        user_unique_id: user_unique_id,
        violation_type: violation.type,
        violation_description: violation.description,
        timestamp: violation.timestamp
      };
      
      // This would need a new API endpoint for security violations
      await postAPI("/exam-security/log-violation", filter);
    } catch (error) {
      console.error('Failed to report security violation:', error);
    }
  };

  const showSecurityWarning = (title, message) => {
    Swal.fire({
      title: title,
      text: message,
      icon: 'warning',
      confirmButtonText: 'I Understand',
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 5000,
      customClass: {
        popup: 'security-warning'
      }
    });
  };

  const enableExamMode = () => {
    setExamStarted(true);
    setKeyboardShortcutsDisabled(true);
    
    // Show exam instructions
    Swal.fire({
      title: 'Exam Security Mode Activated',
      html: `
        <div style="text-align: left;">
          <h5>⚠️ Important Security Guidelines:</h5>
          <ul>
            <li>✅ Stay in fullscreen mode throughout the exam</li>
            <li>✅ Keep your face visible to the camera</li>
            <li>✅ Maintain proper distance from the screen</li>
            <li>❌ Do not switch tabs or minimize the window</li>
            <li>❌ Do not use keyboard shortcuts</li>
            <li>❌ Do not right-click or open context menus</li>
          </ul>
          <p><strong>Violations will be logged and may result in exam termination.</strong></p>
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Start Exam',
      allowOutsideClick: false,
      allowEscapeKey: false
    });
  };

  useEffect(() => {
    fetchResultData(public_access_token);
  }, []);

  
  
    const fetchResultData = async (public_access_token) => {
      const filter = { org_id: org_id, public_access_token: public_access_token, user_unique_id: user_unique_id };
      const response = await postAPI("/results/user-exam-wise-list", filter);
      if (response?.status && response?.data?.length > 0) {
        if (response.data[0]?.is_submit) {
          window.location.href = "/CompleteExam"
        }
      } else {
      }
    };

  const enterFullscreen = () => {
    if (containerRef.current.requestFullscreen) {
      containerRef.current.requestFullscreen();
    } else if (containerRef.current.webkitRequestFullscreen) { /* Safari */
      containerRef.current.webkitRequestFullscreen();
    } else if (containerRef.current.mozRequestFullScreen) { /* Firefox */
      containerRef.current.mozRequestFullScreen();
    } else if (containerRef.current.msRequestFullscreen) { /* IE11 */
      containerRef.current.msRequestFullscreen();
    }
  };

  return (
    <div ref={containerRef} style={{ background: '#f8f7fa', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {!isFullscreen ? (
        <div className='text-center p-4'>
          <div className="card shadow-lg" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className="card-body">
              <h4 className="card-title mb-3">🔒 Secure Exam Mode</h4>
              <p className="card-text mb-4">
                This exam requires fullscreen mode for security. Your activity will be monitored during the exam.
              </p>
              
              {/* Security Status Indicators */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Connection Status:</span>
                  <span className={`badge ${connectionStatus === 'online' ? 'badge-success' : 'badge-danger'}`}>
                    {connectionStatus === 'online' ? '🟢 Online' : '🔴 Offline'}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Security Violations:</span>
                  <span className={`badge ${securityViolations.length === 0 ? 'badge-success' : 'badge-warning'}`}>
                    {securityViolations.length === 0 ? '✅ None' : `⚠️ ${securityViolations.length}`}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Tab Switches:</span>
                  <span className={`badge ${visibilityChangeCount === 0 ? 'badge-success' : 'badge-danger'}`}>
                    {visibilityChangeCount === 0 ? '✅ None' : `❌ ${visibilityChangeCount}`}
                  </span>
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg"
                style={{ minWidth: "200px" }}
                onClick={() => {
                  enterFullscreen();
                  setTimeout(enableExamMode, 1000); // Enable exam mode after fullscreen
                }}
              >
                🚀 Enter Secure Exam Mode
              </button>
              
              <div className="mt-3">
                <small className="text-muted">
                  Press F11 or click the button to enter fullscreen mode
                </small>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="container-fluid h-80vh ExamPortal">
          {/* Security Status Bar */}
          <div className="row mb-2">
            <div className="col-12">
              <div className="card bg-dark text-white">
                <div className="card-body py-2">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <small>🔒 Secure Mode: <span className="text-success">Active</span></small>
                    </div>
                    <div className="col-md-3">
                      <small>🌐 Connection: 
                        <span className={connectionStatus === 'online' ? 'text-success' : 'text-danger'}>
                          {connectionStatus === 'online' ? ' Online' : ' Offline'}
                        </span>
                      </small>
                    </div>
                    <div className="col-md-3">
                      <small>⚠️ Violations: 
                        <span className={securityViolations.length === 0 ? 'text-success' : 'text-warning'}>
                          {securityViolations.length}
                        </span>
                      </small>
                    </div>
                    <div className="col-md-3">
                      <small>💾 Last Save: {lastSaveTime.toLocaleTimeString()}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="container-fluid p-0">
              <ExamAttendComponent 
                onExamStart={enableExamMode}
                securityViolations={securityViolations}
                logSecurityViolation={logSecurityViolation}
                setLastSaveTime={setLastSaveTime}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
